import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryFunctionContext,
  type RefetchOptions,
  type UndefinedInitialDataInfiniteOptions,
  type UndefinedInitialDataOptions,
} from "@tanstack/react-query";
import { useCallback, useMemo, useRef } from "react";

/**
 * Represents pagination parameters.
 */
export type Pagination = Record<"page" | "limit", number>;

/**
 * Determines the refetching mode.
 * - `"remove"`: Removes the data from the cache and triggers a refetch (deleting all data and refetching).
 * - `"invalidate"`: Invalidates the data in the cache, marking it as stale and triggering a refetch on the next access (seamless fetch).
 */
export type RefetchMode = "remove" | "invalidate";

/**
 * A method to fetch paginated data.
 * @template T - The type of data being fetched.
 * @template Dep - The dependencies required for the method.
 * @param pagination - The pagination parameters.
 * @param deps - Additional dependencies.
 * @returns A promise resolving to an array of data.
 */
export type PaginationMethod<T, Dep extends any[] = []> = (
  pagination: Pagination,
  ...deps: Dep
) => Promise<T[]>;

/**
 * A method to fetch the total count of items.
 * @template T - The type of data being counted.
 * @template Dep - The dependencies required for the method.
 * @param deps - Additional dependencies.
 * @returns A promise resolving to the total count of items.
 */
export type PaginationCountMethod<T, Dep extends any[]> = (
  ...deps: Dep
) => Promise<number>;

/**
 * Represents the loading state of pagination.
 */
export type InfinitePaginationLoading =
  | "not-loading"
  | "loading-next"
  | "loading-initial";

export interface PaginationConfigBase<T, Dep extends any[]> {
  /** The function to fetch paginated data. */
  dataFn: PaginationMethod<T, Dep>;
  /** The function to fetch the total count of items. */
  countFn: PaginationCountMethod<T, Dep>;
  /** A unique name for the query. */
  name: string;
  /** Dependencies required for the query. */
  deps: Dep;
}

export interface InfinitePaginationConfig<T, Dep extends any[]>
  extends PaginationConfigBase<T, Dep>,
    Partial<
      Omit<
        UndefinedInitialDataInfiniteOptions<
          { data: T[]; count: number },
          Error,
          InfiniteData<
            {
              data: T[];
              count: number;
            },
            unknown
          >,
          readonly [string, number, ...Dep],
          number | undefined
        >,
        | "meta"
        | "queryKey"
        | "queryFn"
        | "getNextPageParam"
        | "initialPageParam"
        | "getPreviousPageParam"
      >
    > {
  /** The number of rows to fetch per request */
  limit: number;
}

export interface PaginationConfig<T, Dep extends any[]>
  extends PaginationConfigBase<T, Dep>,
    Partial<
      Omit<
        UndefinedInitialDataOptions<
          { data: T[]; count: number },
          Error,
          { data: T[]; count: number },
          readonly [string, number, number, ...Dep]
        >,
        "queryKey" | "queryFn"
      >
    > {
  /** The pagination object. */
  pagination: Pagination;
  /** Callback fired when the pagination is out of query range (i.e. the page is larger than the max page)*/
  onOutOfRange?: VoidFunction;
}

/**
 * Metadata for the pagination query.
 * @template T - The type of data being queried.
 * @template Dep - The dependencies required for the query.
 */
export interface PaginationQueryMeta<T, Dep extends any[]> {
  /** The number of items per page. */
  limit: number;
  /** Dependencies required for the query. */
  deps: Dep;
}

/**
 * A hook to manage paginated data using react-query.
 * @template T - The type of data being paginated.
 * @template Dep - The dependencies required for the hook.
 * @param config - The configuration for the pagination.
 * @returns An object containing paginated data, loading state, and other query information.
 */
export function useInfinitePagination<T, Dep extends any[]>(
  config: InfinitePaginationConfig<T, Dep>,
) {
  const { limit, countFn, dataFn, name, deps, ...queryConfig } = config;

  const totalCount = useRef<number | null>(null);

  const queryKey = [name, limit, ...deps] as const;

  const queryFn = useCallback(
    async (
      params: QueryFunctionContext<typeof queryKey, number | undefined>,
    ) => {
      const { pageParam: page = 1, meta } = params;
      const { deps, limit } = meta as unknown as PaginationQueryMeta<T, Dep>;

      const [data, count] = await Promise.all([
        dataFn({ limit, page }, ...deps),
        page === 1 ? countFn(...deps) : totalCount.current!,
      ]);
      totalCount.current = count;
      return {
        data,
        count,
      };
    },
    [countFn, dataFn, ...deps],
  );

  const meta = {
    limit,
    deps,
  } as PaginationQueryMeta<T, Dep> & Record<string, unknown>;

  const { data, ...rest } = useInfiniteQuery({
    meta,
    queryKey,
    queryFn,
    getNextPageParam: (_lastPage, allPages) => {
      const ttlCount = totalCount.current ?? 0;
      const currentCount = allPages.flatMap((v) => v.data).length;
      if (currentCount < ttlCount) {
        return allPages.length + 1;
      }
    },
    getPreviousPageParam: (_, allPages) =>
      allPages.length <= 1 ? undefined : allPages.length - 1,
    initialPageParam: 1,
    ...queryConfig,
  });

  const flatData = useMemo<T[] | undefined>(
    () => data?.pages.flatMap((v) => v.data),
    [data],
  );

  const paginationLoading: InfinitePaginationLoading = useMemo(() => {
    if (rest.isFetching && !data) {
      return "loading-initial";
    }
    if (rest.isFetchingNextPage) {
      return "loading-next";
    }
    return "not-loading";
  }, [rest.isFetching, rest.isFetchingNextPage, data]);

  return {
    allRows: flatData,
    paginationLoading,
    totalRows: totalCount.current,
    ...rest,
  };
}

export function usePagination<T, Dep extends any[]>(
  config: PaginationConfig<T, Dep>,
) {
  const {
    countFn,
    dataFn,
    name,
    deps,
    pagination,
    onOutOfRange,
    ...queryConfig
  } = config;
  const { limit, page } = pagination;

  const queryKey = [name, page, limit, ...deps] as const;

  const queryClient = useQueryClient();

  const queryFn = useCallback(
    async (_: QueryFunctionContext<typeof queryKey>) => {
      const [data, count] = await Promise.all([
        dataFn({ limit, page }, ...deps),
        countFn(...deps),
      ]);

      const pagesCount = count / limit;

      if (pagesCount !== 0 && (page >= pagesCount || page < 0)) {
        // If the page is invalid
        onOutOfRange?.();
        throw new Error("Query pagination out of range");
      }

      return { data, count };
    },
    [dataFn, countFn, limit, page, onOutOfRange, ...deps, queryClient],
  );

  const { data, refetch, ...rest } = useQuery({
    queryKey,
    queryFn,
    ...queryConfig,
  });

  const refetchData = useCallback<
    (mode: RefetchMode, options?: RefetchOptions) => ReturnType<typeof refetch>
  >(
    async (mode, options) => {
      if (mode === "remove") {
        queryClient?.removeQueries({
          queryKey: [name],
          exact: false,
        });
      } else {
        await queryClient?.invalidateQueries({
          queryKey: [name],
          exact: false,
        });
      }

      return refetch(options);
    },
    [refetch, name, queryClient],
  );

  return {
    currentPageData: data?.data,
    totalRows: data?.count,
    refetch: refetchData,
    ...rest,
  };
}
